import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider, Table } from 'tea-component';
import { metricsMap, metricsObjMap, tipsMap } from '../../../constants';
import { _sorter } from '../../../utils';

import { formatDateFunc } from '@/pages/opinion/utils/time';
import { getChartsShowCount } from '@/pages/opinion/utils';
import HoverSortCommon from '@/pages/opinion/components/HoverSortCommon';
import ChannelLabel from '@/pages/opinion/components/ChannelLabel';
import BubbleCommon from '@/pages/opinion/components/BubbleCommon';
import PercentageItem from '@/pages/opinion/components/Snippets/PercentageItem';
import { getLocale, useIntl } from 'umi';
import { getTeaLocale } from '@/pages/home/utils/lang';
import { NO_DATE } from '@op/constants';

const { pageable, scrollable, sortable, autotip } = Table.addons;

export default function TrendsInfoTable({
  metric,
  channelList,
  tableList,
  dateType,
  loading,
}: any) {
  const { formatMessage } = useIntl();
  const [sort, setSort] = useState<{ by: string; order: 'asc' | 'desc' }>({
    by: 'date',
    order: 'desc',
  });
  const sortChange = (sortItem: any) =>
    setSort(
      sortItem || {
        by: 'date',
        order: 'desc',
      },
    );

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    setPageIndex(1);
    setPageSize(5);
  }, [loading]);

  const columns = useMemo(() => {
    let _columns: Array<any> = [];
    _columns.push({
      key: 'date',
      align: 'left',
      width: channelList.length === 1 ? 'auto' : 200,
      header: (
        <div className="table-header">{formatMessage({ id: 'Date' })}</div>
      ),
      render: (cvm: any) => {
        return (
          <div className="game-table-text">
            {cvm.date == '' ? '-' : formatDateFunc(dateType)(cvm.date)}
          </div>
        );
      },
    });

    channelList.forEach((item: any) => {
      _columns.push({
        key: item,
        align: 'right',
        header: (
          <HoverSortCommon
            sort={sort}
            sortKey={item}
            sortChange={sortChange}
            sortOptions={[
              {
                text: formatMessage({ id: metricsMap[metric] }),
                value: item + '_count',
              },
              {
                text: formatMessage({ id: metricsMap[metric] }) + '%',
                value: item + '_count_than',
              },
            ]}
          >
            <ChannelLabel
              iconClassName="table-header-img"
              showText={false}
              iconStyle={{ marginRight: 4 }}
              channel_name={item === 'facebook' ? 'facebook_gaming' : item}
            ></ChannelLabel>
            <BubbleCommon
              showIcon={false}
              placement="top"
              tipsMap={tipsMap[metric]}
            >
              <p>
                {formatMessage({
                  id: metricsObjMap[metric]?.title || NO_DATE.EMPTY,
                })}
              </p>
              {metricsObjMap[metric].subTitle && (
                <p>
                  {formatMessage({
                    id: metricsObjMap[metric]?.subTitle || NO_DATE.EMPTY,
                  })}
                </p>
              )}
            </BubbleCommon>
          </HoverSortCommon>
        ),
        render: (tableItem: any) => {
          const countKey = item + '_count';
          const countThanKey = item + '_count_than';

          return (
            <PercentageItem percentage={tableItem[countThanKey]}>
              {(element: any) => (
                <>
                  {getChartsShowCount(tableItem[countKey])} {element}
                </>
              )}
            </PercentageItem>
          );
        },
      });
    });
    return _columns;
  }, [tableList, sort, formatMessage]);
  const locale = getTeaLocale(getLocale());
  return (
    <ConfigProvider locale={locale}>
      <Table
        recordKey="date"
        bordered="all"
        className="dbo-community-table"
        records={loading ? [] : tableList.sort(sortable.comparer([sort]))}
        columns={columns}
        addons={[
          scrollable({ maxHeight: 310, scrollToTopOnChange: true }),
          pageable({
            pageIndex: pageIndex,
            pageSize: pageSize,
            stateTextVisible: true,
            pageSizeVisible: tableList?.length > 5,
            pageIndexVisible: tableList?.length > 5,
            jumpVisible: tableList?.length > 5,
            endJumpVisible: tableList?.length > 5,
            pageSizeOptions: [5, 10, 15],
            onPagingChange: (query) => {
              setPageIndex(query.pageIndex || 1);
              setPageSize(query.pageSize || 5);
            },
          }),
          autotip({
            isLoading: loading,
          }),
          sortable({
            columns: [{ key: 'date', prefer: 'desc' }],
            value: [sort],
            onChange: (value) => sortChange(value?.[0]),
          }),
        ]}
      />
    </ConfigProvider>
  );
}
